/* eslint-disable no-inline-styles/no-inline-styles */
'use client';

import React from 'react';

import RichText from 'molecules/RichText';

import styles from 'components/MarketingBanner/marketing-banner.module.scss';

export type SizeToken = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type ThemeMode = 'theme' | 'custom';
export type BannerTheme = 'light' | 'dark' | 'accent';

export type MarketingBannerProps = {
  enabled?: boolean;
  message?: string;

  backgroundMode?: ThemeMode;
  backgroundTheme?: BannerTheme;
  backgroundHex?: string;

  textMode?: ThemeMode;
  textTheme?: BannerTheme;
  textHex?: string;

  fontSizeMode?: ThemeMode;
  fontSizeTheme?: SizeToken;
  fontSizeRem?: string;

  textAlign?: 'left' | 'center' | 'right';

  paddingY?: SizeToken;
  paddingX?: SizeToken;

  siteTarget?: 'LC' | 'HCI' | 'BOTH';
};

const paddingXClassBySize: Record<SizeToken, string> = {
  xs: styles.pxXs,
  sm: styles.pxSm,
  md: styles.pxMd,
  lg: styles.pxLg,
  xl: styles.pxXl,
};

const paddingYClassBySize: Record<SizeToken, string> = {
  xs: styles.pyXs,
  sm: styles.pySm,
  md: styles.pyMd,
  lg: styles.pyLg,
  xl: styles.pyXl,
};

const bgThemeHex: Record<BannerTheme, string> = {
  light: '#FFFFFF',
  dark: '#0F172A',
  accent: '#EEF2FF',
};

const textThemeHex: Record<BannerTheme, string> = {
  light: '#0F172A',
  dark: '#FFFFFF',
  accent: '#312E81',
};

const fontThemeRem: Record<SizeToken, string> = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
};

const normalizeHex = (value?: string): string => {
  if (!value) {
    return '';
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
};

const isHex = (value?: string): boolean => !!value && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value);

const resolveHex = (mode: ThemeMode, customHex: string | undefined, fallback: string): string => {
  if (mode === 'custom') {
    const normalized = normalizeHex(customHex);
    if (isHex(normalized)) {
      return normalized;
    }
  }

  return fallback;
};

const resolveRem = (mode: ThemeMode, customRem: string | undefined, fallback: string): string => {
  if (mode === 'custom') {
    if (customRem && /^(\d+(\.\d+)?)rem$/.test(customRem)) {
      return customRem;
    }
  }

  return fallback;
};

const MarketingBanner: React.FC<MarketingBannerProps> = ({
  enabled = true,
  message = '',

  backgroundMode = 'theme',
  backgroundTheme = 'light',
  backgroundHex = '',

  textMode = 'theme',
  textTheme = 'light',
  textHex = '',

  fontSizeMode = 'custom',
  fontSizeTheme = 'xl',
  fontSizeRem = '',

  textAlign = 'center',

  paddingY = 'sm',
  paddingX = 'md',

  siteTarget = 'BOTH',
}) => {
  if (!enabled || siteTarget === 'LC') {
    return null;
  }

  const trimmedMessage = message.trim();
  if (!trimmedMessage) {
    return null;
  }

  const resolvedBackground = resolveHex(backgroundMode, backgroundHex, bgThemeHex[backgroundTheme]);

  const resolvedText = resolveHex(textMode, textHex, textThemeHex[textTheme]);

  const resolvedFontSize = resolveRem(fontSizeMode, fontSizeRem, fontThemeRem[fontSizeTheme]);

  const wrapperClasses = [styles.wrapper, paddingXClassBySize[paddingX], paddingYClassBySize[paddingY]].join(' ');

  const wrapperStyle: React.CSSProperties = {
    backgroundColor: resolvedBackground,
    color: resolvedText,
    fontSize: resolvedFontSize,
  };

  return (
    <div className={wrapperClasses} style={wrapperStyle} role="region" aria-label="Site announcement">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign,
          width: '100%',
        }}
      >
        <RichText content={trimmedMessage} />
      </div>
    </div>
  );
};

export default MarketingBanner;
